import React from 'react'
import { Button, Tag, Badge } from 'antd'
import { EditOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import cloneDeep from 'lodash/cloneDeep'
import { ReactSortable } from 'react-sortablejs'
import { COLUMNS_INFO } from './../columns'
import {
    getHomeRowBackground,
    getBackgroundTextColor
} from './../../../services/appServices'

const Container = styled.div`
    margin-top: 10px;
    padding: 5px 10px;
    border-radius: 2px;
    background-color: #f6f6f6;
    .drop-area {
        padding: 0px 15px 0px 30px;
        &-level2 {
            background-color: #f6f6f6;
        }
    }
    .sortable-chosen {
        background-color: lightblue;
    }
    .sortable-drag {
        background-color: lightblue;
    }
    .block-item-level2 {
        margin-top: 0px !important;
    }
`

const RowWrapper = styled.div`
    cursor: grab;
    margin-top: 5px;
    margin-bottom: 15px;
    background: #fff;
    border-radius: 2px;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
    background-size: cover;
    background-position: center center;
    ${props => getHomeRowBackground(props.colors, props.image)};
    .drop-area {
        display: flex;
        flex-wrap: wrap;
        padding: 10px;
        min-height: 100px;
    }
`

const ItemContainer = styled.div`
    display: flex;
    justify-content: space-between;
    > .topic {
        flex: 1;
        display: flex;
        padding: 15px;
        > .icon {
            padding-right: 10px;
        }
    }
    > .action {
        cursor: pointer;
        padding: 15px;
        > .wrapper {
            display: flex;
            padding: 0 10px;
            background: #fff;
            border-radius: 50px;
            > .item {
                padding: 5px;
                &:hover {
                    color: #1890ff;
                }
            }
        }
    }
`

const ColumnContainer = styled.div`
    cursor: grab;
    display: flex;
    width: ${props => (props.size * 100) / 12}%;
    padding: 0 10px;
    > .column-item {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 140px;
        margin-bottom: 15px;
        padding: 10px;
        border-radius: 2px;
        background: ${props => props.color};
        > .title {
            font-weight: bold;
            margin-bottom: 5px;
            color: ${props => props.textColor};
        }
        > .type-tag {
            padding-top: 5px;
            padding-bottom: 10px;
            .ant-tag {
                margin: 0;
            }
        }
        > .action {
            cursor: pointer;
            padding: 15px;
            display: flex;
            padding: 0 10px;
            background: #fff;
            border-radius: 50px;
            > .item {
                padding: 5px;
                &:hover {
                    color: #1890ff;
                }
            }
        }
    }
`

const sortableOptions = {
    animation: 150,
    fallbackOnBody: true,
    swapThreshold: 0.8,
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    emptyInsertThreshold: 100,
    forceFallback: true,
    scrollSensitivity: 100,
    group: {
        name: 'nested',
        pull: function (to, from, el) {
            let toLevel = to.options.dataIdAttr
            let type = el.getAttribute('data-type')
            return type == 'column' && toLevel != 1
        }
    }
}

const DragBoard = ({
    T,
    columns,
    setBlocks,
    setState,
    onEditRow,
    onRemoveRow,
    onAddColumn,
    onEditColumn,
    onRemoveColumn
}) => {
    return (
        <Container>
            <ReactSortable
                dataIdAttr={1}
                list={columns}
                setList={setBlocks.bind(this)}
                {...sortableOptions}>
                {columns.map((block, blockIndex) => (
                    <BlockWrapper
                        key={block.id}
                        T={T}
                        block={block}
                        blockIndex={[blockIndex]}
                        setBlocks={setState.bind(this)}
                        onEditRow={onEditRow.bind(this)}
                        onRemoveRow={onRemoveRow.bind(this)}
                        onAddColumn={onAddColumn.bind(this)}
                        onEditColumn={onEditColumn.bind(this)}
                        onRemoveColumn={onRemoveColumn.bind(this)}
                    />
                ))}
            </ReactSortable>
        </Container>
    )
}

function ContainerฺBlock({
    T,
    block,
    blockIndex,
    setBlocks,
    onEditRow,
    onRemoveRow,
    onAddColumn,
    onEditColumn,
    onRemoveColumn
}) {
    block.columns = block.columns || []
    return (
        <ReactSortable
            key={block.id}
            list={block.columns}
            className="drop-area"
            setList={currentList => {
                setBlocks(prevState => {
                    const tempList = cloneDeep(prevState.columns)
                    const _blockIndex = [...blockIndex]
                    const lastIndex = _blockIndex.pop()
                    const lastArr = _blockIndex.reduce(
                        (arr, i) => arr[i]['columns'] || [],
                        tempList
                    )
                    lastArr[lastIndex]['columns'] = currentList
                    return { columns: tempList }
                })
            }}
            {...sortableOptions}>
            {block.columns &&
                block.columns.map((childBlock, index) => {
                    return (
                        <BlockWrapper
                            key={childBlock.id}
                            T={T}
                            block={childBlock}
                            blockIndex={[...blockIndex, index]}
                            setBlocks={setBlocks.bind(this)}
                            onEditRow={onEditRow.bind(this)}
                            onRemoveRow={onRemoveRow.bind(this)}
                            onAddColumn={onAddColumn.bind(this)}
                            onEditColumn={onEditColumn.bind(this)}
                            onRemoveColumn={onRemoveColumn.bind(this)}
                        />
                    )
                })}
        </ReactSortable>
    )
}
function BlockWrapper({
    T,
    block,
    blockIndex,
    setBlocks,
    onEditRow,
    onRemoveRow,
    onAddColumn,
    onEditColumn,
    onRemoveColumn
}) {
    if (!block) return null

    if (block.block_type == 'row') {
        return (
            <RowWrapper
                data-type={block.block_type}
                className={`block`}
                colors={block.background_colors}
                image={block.background_image}>
                <ItemContainer>
                    <div className="topic">
                        <div className="title">
                            <Button onClick={() => onAddColumn(blockIndex)}>
                                <PlusOutlined /> {T('ADD_COLUMN')}
                            </Button>
                        </div>
                    </div>
                    <div className="action">
                        <div className="wrapper">
                            <div
                                className="item"
                                onClick={() => onEditRow(blockIndex, block)}>
                                <EditOutlined />
                            </div>
                            <div
                                className="item"
                                onClick={() => onRemoveRow(blockIndex)}>
                                <DeleteOutlined />
                            </div>
                        </div>
                    </div>
                </ItemContainer>
                <div className={`clearfix`}>
                    <ContainerฺBlock
                        T={T}
                        block={block}
                        blockIndex={blockIndex}
                        setBlocks={setBlocks.bind(this)}
                        onEditRow={onEditRow.bind(this)}
                        onRemoveRow={onRemoveRow.bind(this)}
                        onAddColumn={onAddColumn.bind(this)}
                        onEditColumn={onEditColumn.bind(this)}
                        onRemoveColumn={onRemoveColumn.bind(this)}
                    />
                </div>
            </RowWrapper>
        )
    } else {
        return (
            <ColumnContainer
                data-type={block.block_type}
                size={block.size || 12}
                color={block.color || '#34495e'}
                textColor={getBackgroundTextColor(block.color || '#34495e')}>
                <div className="column-item ant-ribbon-wrapper">
                    <div className="title">{block.title || T('UNTITLED')}</div>
                    <div className="action">
                        <div
                            className="item"
                            onClick={() => onEditColumn(blockIndex, block)}>
                            <EditOutlined />
                        </div>
                        <div
                            className="item"
                            onClick={() => onRemoveColumn(blockIndex)}>
                            <DeleteOutlined />
                        </div>
                    </div>
                    <div className="ant-ribbon ant-ribbon-placement-end ant-ribbon-color-red">
                        <span className="ant-ribbon-text">
                            {COLUMNS_INFO[block.type]?.name}
                        </span>
                        <div className="ant-ribbon-corner" />
                    </div>
                </div>
            </ColumnContainer>
        )
    }
}

export default DragBoard
